import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getToken } from '../auth0';
import Logger from '../common/logger';
import { config } from '../config';

/**
 * Per-request, non-singleton variant of `sendApiRequest`.
 *
 * Mirrors the existing helper in `./index.ts` but takes the tenant as a
 * parameter and never mutates `configuredAxios.defaults.headers.common`.
 * This is what unlocks cross-tenant parallel queries in Master Mode —
 * each call passes its own `X-Tenant` header without trampling the
 * shared singleton used by the regular tenant-scoped UI.
 *
 * Slug remapping (`vonovia_v2` → `vonovia`, `segmenta_v2` → `segmenta`)
 * matches the legacy behavior in `sendApiRequest` so the backend sees
 * the same canonical tenant identifier it has always seen.
 */

// Dedicated axios instance for per-request tenant calls. We still use a
// shared instance for connection pooling, but never mutate its
// `.defaults.headers.common` — every request carries its own headers.
const masterAxios = axios.create();

const normalizeTenantSlug = (tenant: string): string => {
    if (tenant === 'vonovia_v2') return 'vonovia';
    if (tenant === 'segmenta_v2') return 'segmenta';
    return tenant;
};

/**
 * Retry policy for the Master Mode fan-out. The master pages fire one
 * request per tenant (the lockers page three) concurrently against the
 * Lambda backend; under that burst individual requests occasionally come
 * back with a transient error (throttle, cold-start timeout, gateway
 * 5xx). Without a retry a single blip permanently renders the tenant as
 * "could not be loaded" until the user reloads by hand — so transient
 * failures get a bounded retry with backoff, everything else (401/403/
 * 404, …) fails fast.
 */
const TRANSIENT_STATUS_CODES = new Set([429, 500, 502, 503, 504]);
const MAX_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 1000;

const isTransientError = (error: unknown): boolean => {
    if (!axios.isAxiosError(error)) return false;
    // No response at all: network error / timeout — worth retrying.
    if (!error.response) return true;
    return TRANSIENT_STATUS_CODES.has(error.response.status);
};

const describeError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        return error.response ? `HTTP ${error.response.status}` : 'no response (network)';
    }
    return error instanceof Error ? error.message : 'unknown error';
};

const delay = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

export const sendApiRequestForTenant = async (
    tenant: string,
    request: AxiosRequestConfig,
): Promise<AxiosResponse> => {
    // One base URL for every tenant since the retired v1 route was closed
    // (CEO, 2026-09-18) — see DEPRECATED_TENANT_SLUGS in `api/types.ts`.
    const baseURL = config.api.newBaseUrl;
    // The DATABASE is named without the suffix, so the header still drops it.
    // That normalisation stays; only the base-URL branch is gone.
    const normalizedTenant = normalizeTenantSlug(tenant);

    let lastError: unknown;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        // Fresh token per attempt: getTokenSilently serves from cache, and
        // a retry that spans an expiry boundary must not reuse a stale token.
        const token = await getToken();

        const mergedConfig: AxiosRequestConfig = {
            ...request,
            baseURL,
            headers: {
                ...(request.headers ?? {}),
                Authorization: 'Bearer ' + token,
                'X-Tenant': normalizedTenant,
            },
        };

        try {
            return await masterAxios(mergedConfig);
        } catch (error) {
            lastError = error;
            const detail = describeError(error);
            if (attempt < MAX_ATTEMPTS && isTransientError(error)) {
                Logger('API').warn(
                    `[master:${normalizedTenant}] ${request.url} failed (${detail}), retrying (attempt ${attempt}/${MAX_ATTEMPTS})`,
                );
                // Exponential backoff with jitter so the per-tenant retries
                // don't re-burst in lockstep: ~1s, ~2s.
                await delay(BASE_RETRY_DELAY_MS * 2 ** (attempt - 1) * (0.75 + Math.random() * 0.5));
                continue;
            }
            Logger('API').error(
                `[master:${normalizedTenant}] ${request.url} failed (${detail}) after ${attempt} attempt(s)`,
            );
            throw error;
        }
    }
    // Unreachable (the loop either returns or throws), but keeps TS happy.
    throw lastError;
};
